import { cloneElement, useEffect, useRef, useState } from 'react'
import { classNames } from 'utils/dom-helpers'
import { FormErrors } from './types'

import 'react-dropzone-uploader/dist/styles.css'
import ImageFileUploader from 'components/FileUploader/Image'
import Translation from 'components/Translation'
import OptionList, {
    ListFlyFrom,
    OptionListItem,
} from 'components/OptionList'
import { ModalDataAttributes } from '../types'
import {
    DEFAULT_LOCATION,
    MembershipDisplay,
    MembershipType,
} from 'types/membership'
import {
    DropDownList,
    ListItemProps,
    MultiSelect,
    TagData,
} from '@progress/kendo-react-dropdowns'
import { RadioGroup } from '@headlessui/react'
import { Popup } from '@progress/kendo-react-popup'
import { Tenant } from 'types/tenant'
import { WORD_SINGLE_LINE_REGEX } from 'types/common'
import { useAppErrorApiContext } from 'context/AppErrorContext'
import { postApiRequest } from 'utils/fetch-helper'
import { ImageIcon, InfoIcon } from 'utils/custom-icons'
import { getTranslation } from 'utils/language-helper'

function BasicInfo({
    attributes,
    setAttributes,
    translations,
    onCancel,
    onNext,
    tenant,
}: {
    attributes?: ModalDataAttributes
    setAttributes(r: ModalDataAttributes): void
    translations: Record<string, string>
    onCancel(): void
    onNext(): void
    tenant: Tenant
}) {
    const { setGlobalError } = useAppErrorApiContext()
    const loading = attributes && (attributes.loading as boolean)
    const api_error =
        attributes && (attributes.api_error as Record<string, string>)
    const form_errors: FormErrors =
        (attributes && (attributes.form_errors as unknown as FormErrors)) ||
        {}
    const [is_category_opened, toggleCategoryOpen] =
        useState<boolean>(false)
    const [category_name, setMembershipCategory] = useState<string>('')
    const [is_adding_category, toggleCategoryForm] =
        useState<boolean>(false)
    const is_edit = attributes?.id != undefined
    const [membership_image, setMembershipImage] = useState('')
    const [show_upload_view, setShowUploadView] = useState(true)

    const what_primary_location_anchor = useRef<HTMLButtonElement | null>(
        null
    )
    const what_additional_location_anchor =
        useRef<HTMLButtonElement | null>(null)
    const [popup_ref, setPopupRef] = useState<any>()
    const [popup_key, setPopupKey] = useState<string>('')

    useEffect(() => {
        if (attributes?.image_url && !membership_image) {
            setMembershipImage(attributes?.image_url as string)
            setShowUploadView(false)
        }
    }, [attributes])

    function handleCategoryChange({ value: id }: OptionListItem) {
        toggleCategoryOpen(false)
        if (id) {
            setAttributes({
                ...attributes,
                category_id: id,
            })
        }
    }

    function handleNewCategory() {
        toggleCategoryForm(true)
    }

    async function submitNewCategory() {
        if (!WORD_SINGLE_LINE_REGEX.test(category_name)) {
            setGlobalError({ message: 'Invalid Category Name' })
            return
        }

        const category = {
            name: category_name,
            type: 'MEMBERSHIP',
            tenantId: tenant.id as string,
        }

        const created_category = await postApiRequest(
            '/v1/categories',
            category
        )

        if (created_category.id) {
            const membership_categories =
                (attributes?.membership_categories as Record<
                    string,
                    string
                >[]) || []
            membership_categories.push({
                value: created_category.id,
                text: created_category.name,
            })
            setAttributes({
                ...attributes,
                category_id: created_category.id,
                membership_categories,
            })
        } else {
            setGlobalError({ message: created_category?.message as string })
        }

        toggleCategoryOpen(false)
        toggleCategoryForm(false)
    }

    function LocationItemRender(
        li: React.ReactElement<HTMLLIElement>,
        item_props: ListItemProps
    ) {
        const { dataItem } = item_props
        const item_children = (
            <div className="p-2">
                <p className="text-base font-semibold">{dataItem.text}</p>
            </div>
        )

        return cloneElement(li, li.props, item_children)
    }

    function LocationValueRender(
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) {
        if (!value) {
            return element
        }

        const children = [
            <div key={1} className="px-4">
                {value.text}
            </div>,
        ]

        return cloneElement(element, { ...element.props }, children)
    }

    function LocationTagRender(
        tagData: TagData,
        li: React.ReactElement<
            HTMLLIElement,
            string | React.JSXElementConstructor<any>
        >
    ) {
        const children = [
            <div key={1} className="flex gap-2 text-base p-2">
                {li.props.children}
            </div>,
        ]
        return cloneElement(li, li.props, children)
    }

    function UrlPreviewComponent(props: any) {
        return (
            <div className="flex flex-col">
                <div className="w-full m-auto">
                    <img
                        src={props.image_url}
                        className="h-32 rounded-lg"
                        alt={props.image_alt}
                    />
                </div>
                <div className="w-full m-auto">
                    <p className="text-sm my-2 w-full">{props.image_alt}</p>
                    <div className="flex w-full">
                        <button
                            onClick={() => setShowUploadView(true)}
                            className="flex w-full gap-3 items-center bg-primary-light text-white px-2 py-1 rounded-lg text-lg justify-center"
                        >
                            <ImageIcon color="white" />
                            <span>{translations['replace_photo']}</span>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    function showUploadView() {
        if (!show_upload_view) {
            return (
                <UrlPreviewComponent
                    image_url={membership_image as string}
                    image_alt={translations['booking_background_img']}
                />
            )
        } else {
            return (
                <ImageFileUploader
                    tenant_id={tenant.id}
                    accept=".jpeg, .jpg, .gif, .png"
                    onAdd={handleOnAdd}
                    form_context={{
                        attributes: attributes || {},
                        setAttributes: setAttributes as unknown as (
                            data: Record<string, string | File>
                        ) => void,
                    }}
                    addClassNames={{
                        dropzone:
                            'bg-transparent p-0 overflow-hidden h-64 border-0',
                        inputLabel:
                            'text-gray-400 font-medium text-lg flex-grow',
                        submitButtonContainer: 'mt-3',
                        submitButton:
                            'flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-lg font-serif font-light justify-center',
                    }}
                />
            )
        }
    }

    function handleOnAdd(image_url: string) {
        setMembershipImage(image_url)
        setShowUploadView(false)
    }

    return (
        <form method="POST" className="w-full">
            <div className="mb-5">
                {/* <Translation
                        content_key="membership_basic_info_title"
                        translations={translations}
                        render_as="div"
                        className="text-primary text-xl font-medium font-display"
                    /> */}

                <div className="flex items-center py-3">
                    <RadioGroup
                        value={attributes?.membership_type}
                        disabled={is_edit}
                        onChange={(v) => {
                            setAttributes({
                                ...attributes,
                                membership_type: v as string,
                            })
                        }}
                    >
                        {/* <RadioGroup.Label
                                className={classNames(
                                    form_errors.membership_type
                                        ? 'text-red-700'
                                        : '',
                                    'text-lg block pb-4'
                                )}
                            >
                                <Translation
                                    content_key="membership_type"
                                    translations={translations}
                                />
                            </RadioGroup.Label> */}
                        <div className="flex gap-6">
                            {Object.keys(MembershipType).map((v, idx) => {
                                return (
                                    <RadioGroup.Option
                                        key={v}
                                        value={v}
                                        className={''}
                                    >
                                        {({ checked, disabled }) => (
                                            <div
                                                className={classNames(
                                                    'flex items-center gap-3 w-full h-full rounded-lg membershiptype',
                                                    checked
                                                        ? 'border border-[#4B95B7]'
                                                        : 'border border-gray-200 ',
                                                    !disabled
                                                        ? 'cursor-pointer'
                                                        : ''
                                                )}
                                            >
                                                <img
                                                    src={
                                                        checked
                                                            ? MembershipDisplay[
                                                                  idx
                                                              ].selected
                                                            : MembershipDisplay[
                                                                  idx
                                                              ].active
                                                    }
                                                    className={classNames(
                                                        ' w-5',
                                                        checked
                                                            ? 'stroke-[#4B95B7] fill-[#4B95B7]'
                                                            : 'stroke-gray-200 fill-gray-200'
                                                    )}
                                                    alt="Image"
                                                />
                                                <span
                                                    className={classNames(
                                                        'text-base font-medium',
                                                        !checked && disabled
                                                            ? 'text-zinc-400'
                                                            : 'text-primary'
                                                    )}
                                                >
                                                    {
                                                        Object.values(
                                                            MembershipType
                                                        )[idx]
                                                    }
                                                </span>
                                                <span
                                                    title={getTranslation(
                                                        MembershipDisplay[
                                                            idx
                                                        ].translation,
                                                        translations
                                                    )}
                                                >
                                                    <InfoIcon />
                                                </span>
                                                {/* <Translation
                                                                content_key={
                                                                    MembershipDisplay[
                                                                        idx
                                                                    ]
                                                                        .translation
                                                                }
                                                                render_as="label"
                                                                className={classNames(
                                                                    'text-sm font-[450] mt-2',
                                                                    !checked &&
                                                                        disabled
                                                                        ? 'text-zinc-400'
                                                                        : 'text-zinc-500'
                                                                )}
                                                            /> */}
                                            </div>
                                        )}
                                    </RadioGroup.Option>
                                )
                            })}
                        </div>
                    </RadioGroup>
                </div>
            </div>

            <div className="pb-5 lg:flex gap-5">
                <fieldset className="w-1/2">
                    <label
                        htmlFor="name"
                        className={classNames(
                            'aot-form-label text-sm ',
                            form_errors.name ? 'text-red-700' : ''
                        )}
                    >
                        <Translation
                            content_key="name_of_membership"
                            translations={translations}
                        />
                    </label>
                    <input
                        type="text"
                        id="name"
                        className={classNames(
                            'aot-input focus:ring-primary-light focus:border-primary-light block w-full  rounded-md',
                            form_errors.name
                                ? 'border-red-300 bg-red-100'
                                : ''
                        )}
                        onChange={(e) => {
                            setAttributes({
                                ...attributes,
                                name: e.target.value,
                            })
                        }}
                        defaultValue={(attributes?.name as string) || ''}
                    />
                    {form_errors.name && (
                        <span className="text-sm text-red-700">
                            <Translation
                                content_key={form_errors.name}
                                translations={translations}
                            />
                        </span>
                    )}
                    <div className="custom-selectdrop">

                 
                    <Translation
                        className={classNames(
                            'aot-form-label text-sm  mt-5',
                            form_errors.category ? 'text-red-700' : ''
                        )}
                        htmlFor="category"
                        content_key="memberships.membership_category"
                        render_as="label"
                    />
                    <OptionList
                        id="category"
                        error={form_errors.category}
                        position={ListFlyFrom.TOP_RIGHT}
                        onChange={handleCategoryChange}
                        onActivate={() => {
                            toggleCategoryOpen(true)
                        }}
                        defaultValue={attributes?.category_id as string}
                        className="overflow-auto"
                        button_classnames=""
                        options={
                            (attributes?.membership_categories as OptionListItem[]) ||
                            []
                        }
                        static={is_category_opened}
                    >
                        {is_adding_category ? (
                            <div className="bg-white text-primary-light flex flex-col gap-2 w-full h-12">
                                <div className="border-t border-t-gray-300 w-11/12 mx-auto" />
                                <div className="text-primary-light flex items-center gap-2 w-full justify-center cursor-default h-full px-5">
                                    <input
                                        type="text"
                                        className={classNames(
                                            'h-full',
                                            'focus:ring-primary-light focus:border-primary-light',
                                            'w-64 shadow-sm rounded-md flex-1'
                                        )}
                                        onChange={(e) => {
                                            setMembershipCategory(
                                                e.target.value
                                            )
                                        }}
                                        onKeyDown={(e) => {
                                            if (
                                                e.code
                                                    .toUpperCase()
                                                    .indexOf('ENTER') >= 0
                                            ) {
                                                e.preventDefault()
                                                submitNewCategory()
                                            }
                                            if (e.key == ' ') {
                                                e.preventDefault()
                                                setMembershipCategory(
                                                    (prev: string) => {
                                                        return prev + ' '
                                                    }
                                                )
                                            }
                                        }}
                                        value={category_name}
                                    />
                                    <button
                                        type="button"
                                        className="flex items-center bg-primary gap-1 text-white rounded px-2 h-full"
                                        onClick={submitNewCategory}
                                    >
                                        <i className="feather-plus text-lg" />{' '}
                                        <Translation
                                            content_key="add"
                                            render_as="span"
                                        />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div
                                onClick={handleNewCategory}
                                className="bg-white w-full py-2 flex flex-col"
                            >
                                <div className="border-t border-t-gray-300 w-11/12 mx-auto" />
                                <div className="text-primary-light flex items-center gap-4 w-full justify-center cursor-default hover:bg-gray-50 py-2">
                                    <i className="feather-plus text-lg" />{' '}
                                    <Translation
                                        content_key="add_new_category"
                                        render_as="span"
                                    />
                                </div>
                            </div>
                        )}
                    </OptionList>
                    </div>
                    {form_errors.category && (
                        <Translation
                            render_as="span"
                            className="text-sm text-red-700 block"
                            content_key={form_errors.category}
                            translations={translations}
                        />
                    )}
                    <div className="mt-5 custom-selectdrop">
                        <label
                            htmlFor="name"
                            className={classNames(
                                'aot-form-label text-sm flex items-center gap-2',
                                form_errors.primary_location
                                    ? 'text-red-700'
                                    : ''
                            )}
                        >
                            <Translation
                                content_key="primary_location"
                                translations={translations}
                            />
                            <span
                                title={getTranslation(
                                    'whats_primary_location_desc',
                                    translations
                                )}
                            >
                                <InfoIcon />
                            </span>
                            {/* <span
                                        className="text-primary-light ml-3"
                                        ref={what_primary_location_anchor}
                                        onMouseEnter={() => {
                                            setPopupKey(
                                                'whats_primary_location_desc'
                                            )
                                            setPopupRef(
                                                what_primary_location_anchor
                                            )
                                        }}
                                        onMouseLeave={() => setPopupKey('')}
                                    >
                                        <i className="feather-info mr-1" />
                                        <Translation
                                            render_as="label"
                                            content_key="whats_this"
                                        />
                                    </span> */}
                        </label>
                        <DropDownList
                            className="bg-white  block w-full  border rounded-md text-base"
                            iconClassName="feather-chevron-down"
                            data={
                                (attributes?.locations as OptionListItem[]) ||
                                []
                            }
                            value={attributes?.primary_location}
                            textField="text"
                            dataItemKey="value"
                            required={true}
                            itemRender={LocationItemRender}
                            valueRender={LocationValueRender}
                            onChange={(e) => {
                                const primary_location = e.target.value
                                let additional_locations: ModalDataAttributes[] =
                                    []
                                if (primary_location != DEFAULT_LOCATION) {
                                    additional_locations = (
                                        attributes?.locations as ModalDataAttributes[]
                                    ).filter(
                                        (i) =>
                                            i != DEFAULT_LOCATION &&
                                            i != primary_location
                                    )
                                }
                                setAttributes({
                                    ...attributes,
                                    primary_location,
                                    additional_locations,
                                })
                            }}
                            popupSettings={{
                                className: 'z-[102]',
                            }}
                        />
                    </div>
                    <div className="mt-5 custom-selectdrop">
                        <label
                            htmlFor="name"
                            className={classNames(
                                'aot-form-label text-sm flex items-center gap-2',
                                attributes?.primary_location ==
                                    DEFAULT_LOCATION
                                    ? 'text-gray-400'
                                    : ''
                            )}
                        >
                            <Translation
                                content_key="additional_locations"
                                translations={translations}
                            />
                            <span
                                title={getTranslation(
                                    'whats_additional_locations_desc',
                                    translations
                                )}
                            >
                                <InfoIcon />
                            </span>
                            {/* <span
                                className="text-primary-light ml-3"
                                ref={what_additional_location_anchor}
                                onMouseEnter={() => {
                                    setPopupKey(
                                        'whats_additional_locations_desc'
                                    )
                                    setPopupRef(
                                        what_additional_location_anchor
                                    )
                                }}
                                onMouseLeave={() => setPopupKey('')}
                            >
                                <i className="feather-info mr-1" />
                                <Translation
                                    render_as="label"
                                    content_key="whats_this"
                                />
                            </span> */}
                        </label>
                        <MultiSelect
                            className="bg-white  block w-full  border rounded-lg text-base"
                            data={
                                (attributes?.locations as OptionListItem[]) ||
                                []
                            }
                            onChange={(e) => {
                                const values = e.target.value
                                if (
                                    values[values.length - 1] ==
                                    DEFAULT_LOCATION
                                ) {
                                    setAttributes({
                                        ...attributes,
                                        additional_locations: [
                                            DEFAULT_LOCATION,
                                        ],
                                    })
                                } else {
                                    setAttributes({
                                        ...attributes,
                                        additional_locations: values.filter(
                                            (i) =>
                                                i.value !=
                                                DEFAULT_LOCATION.value
                                        ),
                                    })
                                }
                            }}
                            value={
                                attributes?.additional_locations as ModalDataAttributes[]
                            }
                            itemRender={LocationItemRender}
                            tagRender={LocationTagRender}
                            textField="text"
                            dataItemKey="value"
                            disabled={
                                attributes?.primary_location ==
                                DEFAULT_LOCATION
                            }
                            popupSettings={{
                                className: 'z-[102]',
                            }}
                        />
                    </div>
                </fieldset>
                <fieldset className="w-1/2">
                    <Translation
                        translations={translations}
                        content_key="thumbnail_label"
                        render_as="label"
                        className="text-sm block mb-2"
                    />
                    {showUploadView()}
                </fieldset>
            </div>

            <div className="pb-5 lg:flex gap-5">
                <fieldset className="w-full">
                    <Translation
                        className="aot-form-label text-sm"
                        htmlFor="description"
                        content_key="membership_description_label"
                        translations={translations}
                        render_as="label"
                    />
                    <textarea
                        className={classNames(
                            'placeholder-gray-300 ',
                            'focus:ring-primary-light focus:border-primary-light',
                            'block w-full  rounded-md aot-input'
                        )}
                        rows={3}
                        placeholder={
                            translations.description_of_membership ||
                            'description_of_membership'
                        }
                        onChange={(e) => {
                            setAttributes({
                                ...attributes,
                                description: e.target.value,
                            })
                        }}
                        defaultValue={
                            (attributes?.description as string) || ''
                        }
                    />
                </fieldset>
            </div>

            <div>
                {api_error && api_error.message ? (
                    <div className="text-sm text-red-700">
                        {api_error.message}
                    </div>
                ) : (
                    ''
                )}
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        className={classNames(
                            'border border-gray-300 rounded-lg inline-block btn-gap'
                        )}
                        onClick={() => {
                            onCancel()
                        }}
                    >
                        <Translation
                            content_key="cancel"
                            translations={translations}
                        />
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onNext()
                        }}
                        className={classNames(
                            'group flex justify-center',
                            'btn-gap border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            loading
                                ? 'bg-primary-light'
                                : api_error && api_error.message
                                ? 'bg-red-700 hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                                : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                    >
                        <Translation
                            content_key="next"
                            translations={translations}
                        />
                    </button>
                </div>
            </div>

            <Popup
                anchor={popup_ref?.current}
                show={popup_key != ''}
                animate={false}
                popupAlign={{
                    horizontal: 'left',
                    vertical: 'top',
                }}
                popupClass={
                    'bg-black text-white shadow-lg rounded-xl w-96 px-5 py-3 mt-2'
                }
                className="z-[102]"
            >
                <Translation render_as="p" content_key={popup_key} />
            </Popup>
        </form>
    )
}

export default BasicInfo
